<script lang="ts">
  import PubSub from 'pubsub-js'
  import { onMount } from 'svelte'
  import { lang } from '@/store'
  import { i18n, showErrorMessage } from '@/plugins/util'
  import Navbar from '@/components/appDriver/Navbar.svelte'
  import { currentPage } from '@/components/appDriver/store'
  import PageWallet from '@/components/appDriver/wallet/PageWallet.svelte'
  import WalletTransactionItem from '@/components/appDriver/wallet/WalletTransactionItem.svelte'
  import { WALLET_TRANSACTION_FILTER } from '@/components/appDriver/wallet/types'
  import type { WalletTransaction, WalletTransactionFilter } from '@/components/appDriver/wallet/types'
  import { getWalletTransactions } from '@/components/appDriver/wallet/service'
  import axios from 'axios'

  let activeFilter: WalletTransactionFilter = WALLET_TRANSACTION_FILTER.ALL
  let allTransactions: WalletTransaction[] = []
  let extraTransactionsByFilter: Record<Exclude<WalletTransactionFilter, typeof WALLET_TRANSACTION_FILTER.ALL>, WalletTransaction[]> = {
    [WALLET_TRANSACTION_FILTER.DEBIT]: [],
    [WALLET_TRANSACTION_FILTER.CREDIT]: [],
  }
  let visibleTransactions: WalletTransaction[] = []
  let isLoadingTransactions = false
  let isLoadingMore = false
  let errorMessage = ''
  let scrollContainer: HTMLElement
  let lastScrollTop = 0
  let ignoreNextScrollEvent = false
  let hasMoreByFilter: Record<WalletTransactionFilter, boolean> = {
    [WALLET_TRANSACTION_FILTER.ALL]: true,
    [WALLET_TRANSACTION_FILTER.DEBIT]: true,
    [WALLET_TRANSACTION_FILTER.CREDIT]: true,
  }
  let pageByFilter: Record<WalletTransactionFilter, number> = {
    [WALLET_TRANSACTION_FILTER.ALL]: -1,
    [WALLET_TRANSACTION_FILTER.DEBIT]: -1,
    [WALLET_TRANSACTION_FILTER.CREDIT]: -1,
  }

  const filters: Array<{ value: WalletTransactionFilter, label: [string, string] }> = [
    { value: WALLET_TRANSACTION_FILTER.ALL, label: ['All', 'ທັງໝົດ'] },
    { value: WALLET_TRANSACTION_FILTER.DEBIT, label: ['Debit', 'ເງິນອອກ'] },
    { value: WALLET_TRANSACTION_FILTER.CREDIT, label: ['Credit', 'ເງິນເຂົ້າ'] },
  ]

  onMount(() => {
    $currentPage = 'wallet-transactions'
    void fetchInitialTransactions()
  })

  function getTransactionKey(transaction: WalletTransaction): string {
    return `${transaction.id}-${transaction.bookingId ?? ''}-${transaction.createdAt}`
  }

  function mergeUniqueTransactions(
    currentTransactions: WalletTransaction[],
    newTransactions: WalletTransaction[],
  ): WalletTransaction[] {
    const existingKeys = new Set(currentTransactions.map(getTransactionKey))
    const uniqueTransactions = newTransactions.filter((transaction) => !existingKeys.has(getTransactionKey(transaction)))

    return [...currentTransactions, ...uniqueTransactions]
  }

  function getVisibleTransactions(filter: WalletTransactionFilter): WalletTransaction[] {
    if (filter === WALLET_TRANSACTION_FILTER.ALL) {
      return allTransactions
    }

    return mergeUniqueTransactions(
      allTransactions.filter((transaction) => transaction.direction === filter),
      extraTransactionsByFilter[filter],
    )
  }

  function updateVisibleTransactions() {
    visibleTransactions = getVisibleTransactions(activeFilter)
  }

  function getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return error.response.data?.message ?? i18n($lang, ['Cannot load transactions', 'ບໍ່ສາມາດໂຫຼດລາຍການໄດ້'])
      }

      if (error.request) {
        return i18n($lang, ['Cannot connect to server, please try again', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບ ກະລຸນາລອງໃໝ່'])
      }
    }

    return i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່'])
  }

  async function fetchInitialTransactions() {
    isLoadingTransactions = true
    errorMessage = ''

    try {
      const response = await getWalletTransactions(WALLET_TRANSACTION_FILTER.ALL, {
        skip: 0,
        lan: $lang,
      })

      allTransactions = response
      visibleTransactions = response
      hasMoreByFilter[WALLET_TRANSACTION_FILTER.ALL] = response.length > 0
      pageByFilter[WALLET_TRANSACTION_FILTER.ALL] = 0
    } catch (error) {
      errorMessage = getErrorMessage(error)
      showErrorMessage(errorMessage)
    } finally {
      isLoadingTransactions = false
    }
  }

  async function fetchMoreTransactions(filter: WalletTransactionFilter) {
    if (isLoadingTransactions || isLoadingMore || !hasMoreByFilter[filter]) return

    isLoadingMore = true
    errorMessage = ''

    try {
      const nextPage = pageByFilter[filter] + 1
      const response = await getWalletTransactions(filter, {
        skip: nextPage,
        lan: $lang,
      })

      if (filter === WALLET_TRANSACTION_FILTER.ALL) {
        const nextTransactions = mergeUniqueTransactions(allTransactions, response)
        hasMoreByFilter[WALLET_TRANSACTION_FILTER.ALL] = response.length > 0 && nextTransactions.length > allTransactions.length
        pageByFilter[WALLET_TRANSACTION_FILTER.ALL] = nextPage
        allTransactions = nextTransactions
        visibleTransactions = nextTransactions
      } else {
        const currentExtraTransactions = extraTransactionsByFilter[filter]
        const nextExtraTransactions = mergeUniqueTransactions(
          currentExtraTransactions,
          response.filter((transaction) => transaction.direction === filter),
        )

        hasMoreByFilter[filter] = response.length > 0 && nextExtraTransactions.length > currentExtraTransactions.length
        pageByFilter[filter] = nextPage
        extraTransactionsByFilter = {
          ...extraTransactionsByFilter,
          [filter]: nextExtraTransactions,
        }
        updateVisibleTransactions()
      }
    } catch (error) {
      errorMessage = getErrorMessage(error)
      showErrorMessage(errorMessage)
    } finally {
      isLoadingMore = false
    }
  }

  function selectFilter(filter: WalletTransactionFilter) {
    if (activeFilter === filter) {
      if (!isLoadingTransactions && !isLoadingMore && visibleTransactions.length === 0) {
        void fetchInitialTransactions()
      }

      return
    }

    activeFilter = filter
    updateVisibleTransactions()

    if (scrollContainer) {
      lastScrollTop = 0
      ignoreNextScrollEvent = true
      scrollContainer.scrollTop = 0
    }
  }

  function handleScroll() {
    if (!scrollContainer || isLoadingTransactions || isLoadingMore) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer
    const isScrollingDown = scrollTop > lastScrollTop

    lastScrollTop = scrollTop

    if (ignoreNextScrollEvent) {
      ignoreNextScrollEvent = false
      return
    }

    if (!isScrollingDown) return

    if (scrollTop + clientHeight >= scrollHeight - 80) {
      fetchMoreTransactions(activeFilter)
    }
  }
</script>

<div class="page flex h-full flex-col overflow-hidden bg-[#EFEFEF]">
  <div class="shrink-0 bg-[#EFEFEF] pb-4 shadow-sm">
    <Navbar showBack={true} on:back={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageWallet })}/>

    <img src="{new URL('@/assets/general/loca-logo-horizontal.png', import.meta.url).href}" width="100" class="-mt-10 mx-auto" alt="Loca">

    <div class="m-4 mt-6 text-center text-xl font-semibold">
      {i18n($lang, ['Recent Transactions', 'ປະຫວັດລາຍການຫຼ້າສຸດ'])}
    </div>

    <div class="mx-4 grid grid-cols-3 rounded-lg bg-white p-1 shadow">
      {#each filters as filter}
        <button
          type="button"
          class="rounded-md py-2.5 text-center text-sm font-bold uppercase {activeFilter === filter.value ? 'bg-blue-primary text-white' : 'text-gray-400'}"
          on:click={() => selectFilter(filter.value)}
        >
          {i18n($lang, filter.label)}
        </button>
      {/each}
    </div>
  </div>

  <section class="flex-1 space-y-3 overflow-y-auto p-4" bind:this={scrollContainer} on:scroll={handleScroll}>
    {#if isLoadingTransactions}
      {#each Array(4) as _}
        <div class="rounded-lg bg-white p-4 shadow">
          <div class="flex items-center justify-between gap-3 border-b border-gray-200 pb-3">
            <div class="h-3 w-40 animate-pulse rounded bg-gray-200"></div>
            <div class="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
          </div>
          <div class="flex items-center gap-3 pt-4">
            <div class="h-6 w-6 animate-pulse rounded bg-gray-200"></div>
            <div class="min-w-0 flex-1 space-y-2">
              <div class="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
              <div class="h-3 w-24 animate-pulse rounded bg-gray-200"></div>
            </div>
            <div class="h-5 w-20 animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
      {/each}
    {:else if visibleTransactions.length > 0}
      {#each visibleTransactions as transaction (getTransactionKey(transaction))}
        <WalletTransactionItem {transaction}/>
      {/each}

      {#if errorMessage}
        <div class="rounded-md bg-white px-4 py-4 text-center shadow">
          <div class="text-sm text-red-500">{errorMessage}</div>
          <button
            type="button"
            class="mt-3 rounded-md bg-blue-primary px-4 py-2 text-sm font-bold text-white"
            on:click={() => fetchMoreTransactions(activeFilter)}
          >
            {i18n($lang, ['Try again', 'ລອງໃໝ່'])}
          </button>
        </div>
      {:else if isLoadingMore}
        <div class="py-2 text-center text-sm font-semibold text-gray-400">
          {i18n($lang, ['Loading more...', 'ກຳລັງໂຫຼດເພີ່ມ...'])}
        </div>
        {#each Array(2) as _}
          <div class="rounded-lg bg-white p-4 shadow">
            <div class="flex items-center justify-between gap-3 border-b border-gray-200 pb-3">
              <div class="h-3 w-40 animate-pulse rounded bg-gray-200"></div>
              <div class="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
            </div>
            <div class="flex items-center gap-3 pt-4">
              <div class="h-6 w-6 animate-pulse rounded bg-gray-200"></div>
              <div class="min-w-0 flex-1 space-y-2">
                <div class="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                <div class="h-3 w-24 animate-pulse rounded bg-gray-200"></div>
              </div>
              <div class="h-5 w-20 animate-pulse rounded bg-gray-200"></div>
            </div>
          </div>
        {/each}
      {:else if !hasMoreByFilter[activeFilter]}
        <div class="py-3 text-center text-xs text-gray-400">
          {i18n($lang, ['No more transactions', 'ບໍ່ມີລາຍການເພີ່ມແລ້ວ'])}
        </div>
      {/if}
    {:else if errorMessage}
      <div class="rounded-md bg-white px-4 py-8 text-center shadow">
        <div class="text-sm text-red-500">{errorMessage}</div>
        <button
          type="button"
          class="mt-4 rounded-md bg-blue-primary px-4 py-2 text-sm font-bold text-white"
          on:click={() => fetchInitialTransactions()}
        >
          {i18n($lang, ['Try again', 'ລອງໃໝ່'])}
        </button>
      </div>
    {:else}
      <div class="rounded-md bg-white px-4 py-12 text-center text-gray-400">
        {i18n($lang, ['No transactions found', 'ບໍ່ພົບລາຍການ'])}
      </div>
    {/if}
  </section>
</div>
